import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Views, type View } from 'react-big-calendar'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, useTheme } from '@mui/material/styles'
import {
  calendarSearch,
  isCalendarView,
  parseCalendarState,
  PERMISSIONS,
  TEN_DAY_SPAN,
  type CalendarViewName,
} from '@jupi/shared'
import { formatDate, IconButton, RemoteContent, Title, useAuth } from '@jupi/ui'
import BookingCalendar from '../features/calendar/BookingCalendar'
import FleetDayGrid from '../features/calendar/FleetDayGrid'
import { useAircraft, useAircraftDefects, useBookings, useMaintenanceEvents } from '../api/queries'
import { aircraftStatuses, defectsLink } from '../features/calendar/aircraftStatus'
import { useBookingLinkState } from '../features/calendar/bookingNavigation'
import { TEN_DAY_VIEW } from '../features/calendar/TenDayView'

// "fleet" and "tenDay" are ours, not rbc's -- see FleetDayGrid and TenDayView.
const FLEET_VIEW = 'fleet' as const
type CalendarView = CalendarViewName

const LAST_VIEW_KEY = 'calendar.view'

// Per-browser convenience; the page works without it.
function rememberedView(): CalendarView | null {
  try {
    const stored = localStorage.getItem(LAST_VIEW_KEY)
    return isCalendarView(stored) ? stored : null
  } catch {
    return null
  }
}
function rememberView(view: CalendarView) {
  try {
    localStorage.setItem(LAST_VIEW_KEY, view)
  } catch {
    // private window or blocked storage
  }
}

export default function CalendarPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const { can } = useAuth()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))

  // The view, date and aircraft live in the URL, so the booking form can send
  // the member back to exactly this calendar. Without a ?view= a phone opens
  // on the agenda (a scrollable list beats a month grid with 3mm cells) and
  // everyone else on the view they used last, else the week.
  const [searchParams, setSearchParams] = useSearchParams()
  const fromUrl = useMemo(() => parseCalendarState(searchParams), [searchParams])
  const view: CalendarView = fromUrl.view ?? (isPhone ? Views.AGENDA : (rememberedView() ?? Views.WEEK))
  const aircraftFilter = fromUrl.aircraft
  const urlDate = fromUrl.date?.getTime()
  const date = useMemo(() => (urlDate === undefined ? new Date() : new Date(urlDate)), [urlDate])

  const update = useCallback(
    (next: { view?: CalendarView; date?: Date; aircraft?: string | null }) => {
      const merged = {
        view,
        date,
        aircraft: aircraftFilter,
        ...next,
      }
      if (next.view) rememberView(next.view)
      setSearchParams(calendarSearch(merged), { replace: true })
    },
    [view, date, aircraftFilter, setSearchParams],
  )
  const setView = (next: CalendarView) => update({ view: next })
  const setDate = (next: Date) => update({ date: next })
  const setAircraftFilter = (registration: string | null) => update({ aircraft: registration })
  // Handed to the booking form so Save/Cancel return here.
  const fromCalendar = { state: useBookingLinkState() }

  const range = useMemo(() => rangeFor(view, date), [view, date])
  const { data: aircraft = [], isLoading: loadingAircraft, error: aircraftError } = useAircraft()
  const {
    data: bookings = [],
    isLoading,
    error,
  } = useBookings({ from: range.from, to: range.to, registration: aircraftFilter ?? undefined })

  // Defects as the calendar shows them: AOG blocks and restriction warnings.
  const { data: defects } = useAircraftDefects()
  const { data: maintenanceEvents } = useMaintenanceEvents()
  const statuses = useMemo(
    () => aircraftStatuses(defects?.published ?? [], undefined, maintenanceEvents ?? []),
    [defects, maintenanceEvents],
  )
  const openDefects = (registration: string) => navigate(defectsLink(registration))
  const selectedStatus = aircraftFilter && statuses.get(aircraftFilter)?.light !== 'airworthy' ? statuses.get(aircraftFilter) : undefined
  // "No night flying", "AOG until 25.10." and the like, in the member's language.
  const statusText = (status: NonNullable<ReturnType<typeof statuses.get>>) =>
    status.light === 'aog'
      ? status.aog?.until
        ? t('aircraftDefect.aogUntil', { date: formatDate(`${status.aog.until}T12:00:00`) })
        : t('calendar.aogBooking')
      : status.restrictions.map((r) => t(`aircraftDefect.restrictions.${r}`)).join(', ') || t('calendar.restricted')

  const visibleAircraft = useMemo(
    () => (aircraftFilter ? aircraft.filter((a) => a.registration === aircraftFilter) : aircraft),
    [aircraft, aircraftFilter],
  )

  const step = (direction: -1 | 1) => setDate(shift(date, view, direction))

  return (
    <Box>
      <Title label={t('calendar.title')} />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
      >
        <Button size="small" variant="outlined" onClick={() => setDate(new Date())}>
          {t('common.today')}
        </Button>
        <IconButton size="small" onClick={() => step(-1)} aria-label={t('calendar.previous')}>
          <Icon icon="mdi:chevron-left" />
        </IconButton>
        <IconButton size="small" onClick={() => step(1)} aria-label={t('calendar.next')}>
          <Icon icon="mdi:chevron-right" />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
          {formatDate(date)}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, next: CalendarView | null) => next && setView(next)}
        >
          <ToggleButton value={FLEET_VIEW}>{t('calendar.fleet')}</ToggleButton>
          <ToggleButton value={Views.WEEK}>{t('calendar.week')}</ToggleButton>
          <ToggleButton value={TEN_DAY_VIEW}>{t('calendar.tenDays')}</ToggleButton>
          <ToggleButton value={Views.MONTH}>{t('calendar.month')}</ToggleButton>
          <ToggleButton value={Views.AGENDA}>{t('calendar.agenda')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Aircraft filter. Scrolls horizontally on a phone rather than
          wrapping into three rows of chips above a shrinking calendar. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
        }}
      >
        <Chip
          label={t('calendar.allAircraft')}
          color={aircraftFilter === null ? 'primary' : 'default'}
          onClick={() => setAircraftFilter(null)}
        />
        {aircraft.map((plane) => {
          const status = statuses.get(plane.registration)
          const flaggedStatus = status && status.light !== 'airworthy' ? status : null
          const chip = (
            <Chip
              key={plane.registration}
              label={plane.registration}
              color={aircraftFilter === plane.registration ? 'primary' : 'default'}
              onClick={() => setAircraftFilter(plane.registration)}
              icon={
                flaggedStatus ? (
                  <Icon
                    icon={flaggedStatus.light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'}
                    width={18}
                    color={aircraftFilter === plane.registration ? undefined : flaggedStatus.light === 'aog' ? theme.palette.error.main : theme.palette.warning.main}
                  />
                ) : undefined
              }
            />
          )
          return flaggedStatus ? (
            <Tooltip key={plane.registration} title={statusText(flaggedStatus)}>
              {chip}
            </Tooltip>
          ) : (
            chip
          )
        })}
      </Stack>

      {/* The selected aircraft's defect, on one compact line (the chips above
          carry the warning icon for every aircraft). Wraps on a phone. */}
      {selectedStatus ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 2,
            px: 1.5,
            py: 0.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            borderRadius: 2,
            bgcolor: alpha(selectedStatus.light === 'aog' ? theme.palette.error.main : theme.palette.warning.main, 0.12),
            color: selectedStatus.light === 'aog' ? 'error.dark' : 'warning.dark',
          }}
        >
          <Icon icon={selectedStatus.light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'} width={18} />
          <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
            <strong>{aircraftFilter}</strong>
            {' · '}
            {statusText(selectedStatus)}
          </Typography>
          <Button color="inherit" size="small" onClick={() => openDefects(aircraftFilter!)}>
            {t('calendar.openDefects')}
          </Button>
        </Stack>
      ) : null}

      <RemoteContent isLoading={isLoading || loadingAircraft} error={error ?? aircraftError} minHeight={400}>
        {view === FLEET_VIEW ? (
          <FleetDayGrid
            date={date}
            aircraft={visibleAircraft}
            bookings={bookings}
            statuses={statuses}
            onOpenDefects={openDefects}
            onSelectBooking={(booking) => navigate(`/bookings/${booking.bookingId}`, fromCalendar)}
            onSelectSlot={
              can(PERMISSIONS.bookingCreate)
                ? (registration, startsAt, endsAt) =>
                    navigate(
                      `/bookings/new?registration=${registration}&start=${startsAt.toISOString()}${
                        endsAt ? `&end=${endsAt.toISOString()}` : ''
                      }`,
                      fromCalendar,
                    )
                : undefined
            }
          />
        ) : (
          <BookingCalendar
            view={view as View}
            date={date}
            bookings={bookings}
            aircraft={aircraft}
            statuses={statuses}
            onOpenDefects={openDefects}
            onView={(next) => setView(next as CalendarView)}
            onNavigate={setDate}
            onSelectBooking={(booking) => navigate(`/bookings/${booking.bookingId}`, fromCalendar)}
            onSelectSlot={
              can(PERMISSIONS.bookingCreate)
                ? (start, end) =>
                    navigate(
                      `/bookings/new?start=${start.toISOString()}${end ? `&end=${end.toISOString()}` : ''}`,
                      fromCalendar,
                    )
                : undefined
            }
          />
        )}
      </RemoteContent>
    </Box>
  )
}

// The window of bookings to fetch. Padded a day either side so a booking
// starting late on the last day still renders its leading edge.
function rangeFor(view: CalendarView, date: Date): { from: Date; to: Date } {
  const from = new Date(date)
  const to = new Date(date)

  if (view === Views.MONTH) {
    from.setDate(1)
    to.setMonth(to.getMonth() + 1, 1)
  } else if (view === Views.WEEK) {
    from.setDate(from.getDate() - ((from.getDay() + 6) % 7))
    to.setTime(from.getTime())
    to.setDate(to.getDate() + 7)
  } else if (view === TEN_DAY_VIEW) {
    to.setDate(to.getDate() + TEN_DAY_SPAN)
  } else if (view === Views.AGENDA) {
    to.setDate(to.getDate() + 30)
  } else {
    to.setDate(to.getDate() + 1)
  }

  from.setHours(0, 0, 0, 0)
  from.setDate(from.getDate() - 1)
  to.setHours(23, 59, 59, 999)
  to.setDate(to.getDate() + 1)
  return { from, to }
}

function shift(date: Date, view: CalendarView, direction: -1 | 1): Date {
  const next = new Date(date)
  if (view === Views.MONTH) next.setMonth(next.getMonth() + direction)
  else if (view === Views.WEEK) next.setDate(next.getDate() + 7 * direction)
  else if (view === TEN_DAY_VIEW) next.setDate(next.getDate() + TEN_DAY_SPAN * direction)
  else if (view === Views.AGENDA) next.setDate(next.getDate() + 30 * direction)
  else next.setDate(next.getDate() + direction)
  return next
}
